import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import PageShell from '../components/PageShell';
import { orcamentoOptions, siteConfig } from '../data/site';

const initialForm = {
  nome: '',
  telefone: '',
  tipoServico: '',
  nicho: '',
  descricao: '',
  situacaoSite: '',
  prazo: '',
  origem: '',
  website: '',
};

const fieldClass =
  'w-full rounded-xl border border-brown/15 dark:border-burnt/20 bg-white dark:bg-dark-surface px-4 py-3 text-dark dark:text-cream font-body placeholder:text-dark/40 dark:placeholder:text-cream/40 focus:outline-none focus:border-burnt focus:ring-2 focus:ring-burnt/20 transition-colors';

const labelClass = 'block text-sm font-semibold text-dark dark:text-cream mb-2 font-body';

const Orcamento = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(siteConfig.formEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Não foi possível enviar. Tente novamente ou fale pelo WhatsApp.');
      }

      setStatus('success');
      navigate('/obrigado');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error.message || 'Não foi possível enviar agora. Tente pelo WhatsApp ou tente novamente em instantes.'
      );
    }
  };

  return (
    <PageShell>
      <section className="pt-28 pb-20 md:pt-32 md:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-burnt hover:text-primary-700 transition-colors mb-8 font-body"
          >
            <FaArrowLeft />
            Voltar ao site
          </Link>

          <div className="mb-10">
            <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-3">
              Orçamento
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
              Solicitar orçamento
            </h1>
            <p className="text-lg text-dark/70 dark:text-cream/70 font-body leading-relaxed">
              Preencha o formulário abaixo. Analiso seu projeto e retorno em até 24 horas pelo WhatsApp.
            </p>
            <p className="text-sm text-dark/50 dark:text-cream/50 font-body mt-2">
              Leva cerca de 2 minutos.
            </p>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-brown/10 dark:border-burnt/10 bg-white dark:bg-dark-surface shadow-card dark:shadow-card-dark p-6 md:p-10 space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="nome" className={labelClass}>
                Nome completo *
              </label>
              <input
                id="nome"
                name="nome"
                type="text"
                required
                value={form.nome}
                onChange={handleChange}
                className={fieldClass}
                placeholder="Como posso te chamar?"
              />
            </div>

            <div>
              <label htmlFor="telefone" className={labelClass}>
                WhatsApp ou telefone *
              </label>
              <input
                id="telefone"
                name="telefone"
                type="tel"
                required
                value={form.telefone}
                onChange={handleChange}
                className={fieldClass}
                placeholder="(14) 99999-9999"
              />
              <p className="mt-2 text-xs text-dark/50 dark:text-cream/50 font-body">
                Usarei esse contato para retornar seu orçamento.
              </p>
            </div>

            <div>
              <label htmlFor="tipoServico" className={labelClass}>
                O que você precisa? *
              </label>
              <select
                id="tipoServico"
                name="tipoServico"
                required
                value={form.tipoServico}
                onChange={handleChange}
                className={fieldClass}
              >
                <option value="">Selecione o tipo de serviço</option>
                {orcamentoOptions.tipoServico.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="nicho" className={labelClass}>
                Qual é o nicho do seu negócio? *
              </label>
              <input
                id="nicho"
                name="nicho"
                type="text"
                required
                value={form.nicho}
                onChange={handleChange}
                className={fieldClass}
                placeholder="Ex.: clínica odontológica, academia, advocacia, chaveiro..."
              />
            </div>

            <div>
              <label htmlFor="descricao" className={labelClass}>
                Conte um pouco sobre o que você precisa *
              </label>
              <textarea
                id="descricao"
                name="descricao"
                required
                rows={4}
                value={form.descricao}
                onChange={handleChange}
                className={`${fieldClass} resize-y min-h-[120px]`}
                placeholder="Ex.: quero um site para apresentar meus serviços, aparecer no Google e receber contatos pelo WhatsApp."
              />
            </div>

            <div>
              <label htmlFor="situacaoSite" className={labelClass}>
                Você já tem site? *
              </label>
              <select
                id="situacaoSite"
                name="situacaoSite"
                required
                value={form.situacaoSite}
                onChange={handleChange}
                className={fieldClass}
              >
                <option value="">Selecione uma opção</option>
                {orcamentoOptions.situacaoSite.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="prazo" className={labelClass}>
                Qual prazo você imagina para o projeto? *
              </label>
              <select
                id="prazo"
                name="prazo"
                required
                value={form.prazo}
                onChange={handleChange}
                className={fieldClass}
              >
                <option value="">Selecione um prazo</option>
                {orcamentoOptions.prazo.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="origem" className={labelClass}>
                Como me encontrou? <span className="font-normal text-dark/50 dark:text-cream/50">(opcional)</span>
              </label>
              <select
                id="origem"
                name="origem"
                value={form.origem}
                onChange={handleChange}
                className={fieldClass}
              >
                <option value="">Selecione uma opção</option>
                {orcamentoOptions.origem.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {status === 'error' && (
              <div className="rounded-xl border border-red-300/40 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300 font-body">
                {errorMessage}
              </div>
            )}

            <div className="pt-2">
              <motion.button
                type="submit"
                disabled={status === 'loading'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-burnt hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-premium"
                whileHover={status !== 'loading' ? { scale: 1.02 } : undefined}
                whileTap={status !== 'loading' ? { scale: 0.98 } : undefined}
              >
                <FaPaperPlane />
                {status === 'loading' ? 'Enviando...' : 'Enviar solicitação de orçamento'}
              </motion.button>
              <p className="mt-4 text-xs text-dark/50 dark:text-cream/50 font-body">
                Ao enviar, você concorda em ser contatada para retorno do orçamento.
              </p>
            </div>
          </motion.form>
        </div>
      </section>
    </PageShell>
  );
};

export default Orcamento;
